export interface CustomEquality<T> {
	equals(other: T): boolean;
}


/**
 * A set of unique items that have custom equality criteria, as opposed to the standard Set class
 * which uses reference equality (===).
 *
 * Queries and updates are all O(N), so you should prefer a standard Set (O(1)) if possible.
 */
export class CustomEqualitySet<T extends CustomEquality<T>> {
	constructor(items?: Iterable<T>) {
		if (items != null) {
			// TODO: Remove the Array.from() once we upgrade TypeScript to support iterators
			for (let item of Array.from(items)) {
				this.add(item);
			}
		}
	}

	private _items: Array<T> = [];

	get size(): number {
		return this._items.length;
	}

	// Not part of standard Set API. It's useful in a CustomEqualitySet because you might want to get
	// back the exact reference of the element in the collection.
	get(value: T): T {
		return this._items.find(x => x.equals(value));
	}

	has(value: T): boolean {
		return this.get(value) != null;
	}

	add(value: T): this {
		this.addAndGet(value);
		return this;
	}

	// Not part of standard Set API. Again, useful if you want the reference of the existing item.
	addAndGet(value: T): T {
		let existing = this.get(value);
		if (existing != null)
			return existing;

		this._items.push(value);
		return value;
	}

	// The standard Set#delete() returns a boolean. Here we return the actual item deleted.
	delete(value: T): T {
		let index = this._items.findIndex(x => x.equals(value));
		if (index < 0)
			return undefined;
		return this._items.splice(index, 1)[0];
	}

	clear(): void {
		this._items = [];
	}


	forEach(callbackfn: (value: T, value2: T, set: CustomEqualitySet<T>) => void, thisArg?: any): void {
		this._items.forEach(value => {
			callbackfn.call(thisArg, value, value, this);
		});
	}


	toJSON(): any {
		// Standard Sets and Maps are not serialized to JSON. Even though this behavior usually isn't
		// what we want, I think it's better to be consistent
		return undefined;
	}


	[Symbol.toStringTag]: "CustomEqualitySet";

	[Symbol.iterator](): IterableIterator<T> {
		return this.values();
	}

	keys(): IterableIterator<T> {
		return this.values();
	}

	values(): IterableIterator<T> {
		return this._items.values();
	}

	entries(): IterableIterator<[T, T]> {
		let innerIterator = this.values();

		let iterator = {
			[Symbol.iterator]() {
				return iterator;
			},

			next() {
				let result = innerIterator.next();
				let value = result.value != null ? [result.value, result.value] as [T, T] : null;
				return {
					done: result.done,
					value,
				};
			},
		};

		return iterator;
	}

	// Not part of standard Set API.
	// Provides a "deep" comparison, matching up items in each set.
	equals(other: CustomEqualitySet<CustomEquality<T>>): boolean {
		if (other == null || this.size !== other.size)
			return false;
		else {
			// Match up items in each set (by elimination)
			const otherItems = new CustomEqualitySet<CustomEquality<T>>(other);
			for (let item of this._items) {
				if (!otherItems.delete(item))
					return false;
			}
			return otherItems.size === 0;
		}
	}
}
